import { useForm } from '../../contexts/FormContext'
import { Button, Badge, SaveButton } from '../../components/common'
import { Checkbox } from '../../components/forms'
import { TEMPLATES, TEMPLATE_CATEGORIES } from '../../data/templates'
import './Step2DesignBase.css'

function Step2DesignBase() {
  const { formData, updateFormData, goToNextStep, goToPreviousStep } = useForm()

  const handleTemplateSelect = (templateId) => {
    updateFormData({
      designApproach: 'template',
      selectedTemplate: templateId
    })
  }

  const handleCustomDesign = (checked) => {
    if (checked) {
      updateFormData({
        designApproach: 'custom',
        selectedTemplate: '',
        costs: {
          ...formData.costs,
          setupTotal: formData.costs.setupTotal + 30000
        },
        timeline: {
          estimatedWeeks: 3,
          estimatedDays: 0
        }
      })
    } else {
      updateFormData({
        designApproach: '',
        costs: {
          ...formData.costs,
          setupTotal: formData.costs.setupTotal - 30000
        },
        timeline: {
          estimatedWeeks: 2,
          estimatedDays: 0
        }
      })
    }
  }

  const canProceed = formData.designApproach === 'template' ? formData.selectedTemplate : formData.designApproach === 'custom'

  return (
    <div className="step-section">
      {/* Section Header */}
      <div className="section-header">
        <h2>Step 2: Select Your Design Starting Point</h2>
        <p className="section-subtitle">Choose a template or request custom design</p>
      </div>

      {/* Section Body */}
      <div className="section-body">
        {/* Introduction */}
        <div className="intro-text">
          <p>These templates are <strong>design inspiration and starting points only</strong> - the final website will not look exactly like these demos. We'll create a custom design based on your chosen template style, optimized for Japanese/English standards, and tailored to your specific content and brand requirements.</p>
        </div>

        {/* Template Grid by Category */}
        <div className="templates-section">
          {TEMPLATE_CATEGORIES.map(category => {
            const categoryTemplates = TEMPLATES.filter(t => t.category === category)
            if (categoryTemplates.length === 0) return null

            return (
              <div key={category} className="template-category">
                <h3 className="category-title">{category}</h3>
                <div className="templates-grid">
                  {categoryTemplates.map(template => (
                    <div
                      key={template.id}
                      className={`template-card ${formData.selectedTemplate === template.id ? 'selected' : ''}`}
                      onClick={() => handleTemplateSelect(template.id)}
                    >
                      <div className="template-preview">
                        <img
                          src={template.imageUrl}
                          alt={template.name}
                          className="template-image"
                          loading="lazy"
                          onError={(e) => {
                            e.target.style.display = 'none'
                            const placeholder = e.target.parentElement.querySelector('.template-placeholder')
                            if (placeholder) {
                              placeholder.style.display = 'flex'
                            }
                          }}
                        />
                        <div className="template-placeholder">
                          {template.name}
                        </div>
                      </div>
                      <div className="template-info">
                        <div className="template-name">{template.name}</div>
                        <div className="template-description">{template.description}</div>
                        <a
                          href={template.demoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="template-demo-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View Demo →
                        </a>
                      </div>
                      {formData.selectedTemplate === template.id && (
                        <div className="template-selected-badge">✓</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>

        {/* Custom Design Option */}
        <div className="custom-design-section">
          <div className="custom-design-card">
            <div className="custom-design-header">
              <h4>💡 Need something completely unique?</h4>
            </div>
            <div className="custom-design-body">
              <Checkbox
                id="customDesign"
                label="Request Custom Design Services"
                checked={formData.designApproach === 'custom'}
                onChange={(e) => handleCustomDesign(e.target.checked)}
              />
              <div className="custom-design-details">
                <p><strong>Includes:</strong> Custom wireframe + design concept</p>
                <p><strong>Cost:</strong> +¥30,000 (wireframe) + ¥50,000 (logo if needed)</p>
                <p><strong>Timeline:</strong> +3-4 weeks to timeline</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Actions */}
      <div className="section-actions">
        <Button variant="secondary" size="md" onClick={goToPreviousStep}>
          ← Back
        </Button>
        <div className="actions-right">
          <SaveButton />
          <Button variant="primary" size="md" disabled={!canProceed} onClick={goToNextStep}>
            Next Step
          </Button>
        </div>
      </div>
    </div>
  )
}

export default Step2DesignBase
